// src/components/TermsAndConditions.jsx (Stub for Workflow)
import React, { useState } from 'react';
import { IMaskInput } from 'react-imask';
import { Link } from 'react-router-dom';
import { isActivatedComplete, useUser} from '../contexts/UserContext';

function Consent({onSuccess, error, setError, initialData, isWorkflowStep, onSaveComplete, onSaveSuccess, onDirty  }) {
  const {updateUserInfo, refreshUserInfo, user} = useUser();
  const [localError, setLocalError] = useState(false);
  const [uiData, setUIData] = useState({});

  const dataSource = isWorkflowStep ? initialData : user;

  const [consentData, setConsentData] = useState({
    is_hipaa_granted: isActivatedComplete(dataSource?.is_hipaa_granted),
    personal_physician: dataSource?.personal_physician || '',
    physician_number: dataSource?.physician_number || '',
    additional_name: dataSource?.additional_name || '',
    additional_email: dataSource?.additional_email || '',
    additional_phone: dataSource?.designatedPersonPhone || '',
    is_opt_in: isActivatedComplete(dataSource?.is_opt_in), // Handle boolean dataSource
    terms_accepted: isActivatedComplete(dataSource?.terms_accepted), // Handle boolean dataSource
    registration_step: isWorkflowStep ? "terms" : dataSource?.registration_step,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    onDirty?.();

    // UI artifacts don't belong in the consentData 
    if (name.includes('_check')) {
        setUIData(prev => ({ ...prev, [name]: checked }));
        return;
    };

    setConsentData(prev => ({ ...prev, [name]: checked }));
    
    // Clear error if user checks the required box
    if (name === 'terms_accepted' && checked) {
        isWorkflowStep ? setError(''): setLocalError('');
        setConsentData(prev => ({ ...prev, activated: checked}));
    } else if (name === 'terms_accepted') {
      isWorkflowStep ? 
      setError('You must accept the Terms & Conditions to proceed.')
      : 
      setLocalError('Terms acceptance is required. To revoke consent, please delete your account in the Accounts tab.')

    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onDirty?.();
    setConsentData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    isWorkflowStep ? setError(''): setLocalError('');

    // --- Validation ---
    if (!consentData.terms_accepted) {
      isWorkflowStep ?
      setError('You must accept the Terms & Conditions to proceed.')
      :
      setLocalError('Terms acceptance is required. To revoke consent, please delete your account in the Accounts tab.');
      return; // Stop submission
    }

    setIsSubmitting(true);
    
    // attempting recovery
    const userid =  initialData?.userid || user?.userid || localStorage.getItem('redimdwm_userid');

    // can't recover userid
    if (!userid) {
      isWorkflowStep ?
      setError('User information is missing. Cannot save consents.')
      :
      setLocalError('User information is missing. Cannot save consents.');
      setIsSubmitting(false);
      return;
    }

    // userid recovered
    if(!user?.userid) {
      await refreshUserInfo();
    }

    const nextConsentData = {
        ...consentData,
        registration_step: "terms",
        access_types: "user",
    };
    setConsentData(nextConsentData);

    try {
      //const response = await api.updateUserInfo({
      const response = await updateUserInfo(nextConsentData);

      if (response.success) {
        // Pass the full consent data up to the parent workflow state
        // The parent's handleNextStep already merges this into initialData
        if (isWorkflowStep) {
          onSuccess(nextConsentData);
        } else {
          onSaveSuccess?.();
        }
      } else {
        isWorkflowStep ?
        setError(response.error || 'Failed to save consents. Please try again.')
        :
        setLocalError(response.error || 'Failed to save consents. Please try again.');
      }
    } catch {
      isWorkflowStep ?
      setError('An error occurred while saving your preferences. Please try again.')
      :
      setLocalError('An error occurred while saving your preferences. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkBoxClasses = "h-6 w-6 flex-grow-0 flex-shrink-0 self-start border-2 rounded border-neutral-dark text-primary focus:ring-2 focus:ring-primary checked:bg-primary";

  return (
    <div id="consent" className="flex flex-col h-full flex-grow max-h-min">
      <h3 className="text-lg font-semibold text-primary mb-4">Terms and Consent</h3>
      <p className="mb-4 text-sm text-neutral-dark">
        Please review the following statements and check the boxes to indicate your consent and agreement. Acceptance of the main Terms & Conditions is required.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col h-full flex-grow "> {/* Increased spacing */}
        {/* 1. Share with Insurance */}
        <div id="consent_1" className="flex flex-col justify-between flex-grow h-full">
          <div className="flex flex-col flex-grow space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_hipaa_granted"
                name="is_hipaa_granted"
                checked={consentData.is_hipaa_granted}
                onChange={handleCheckboxChange}
                className={checkBoxClasses}
                disabled={isSubmitting}
              />
              <label htmlFor="is_hipaa_granted" className="ml-3 block text-sm text-neutral-dark">
                I consent to RediMD sharing necessary information with my insurance provider for billing and coverage purposes, if applicable.
              </label>
            </div>

            {/* 2. Contact Primary Doctor (UI toggle only) */}
            <div className="flex items-center pt-6 border-t border-neutral-light">
              <input
                type="checkbox"
                id="contactPrimaryDoctor_check"
                name="contactPrimaryDoctor_check"
                onChange={handleCheckboxChange}
                checked={uiData.contactPrimaryDoctor_check}
                className={checkBoxClasses}
                disabled={isSubmitting}
              />
              <label htmlFor="contactPrimaryDoctor_check" className="ml-3 block text-sm text-neutral-dark">
                I authorize RediMD providers to contact my primary care physician if needed for coordination of care.
              </label>
            </div>

            {/* 2a. Primary Care Doctor Info */}
            {uiData.contactPrimaryDoctor_check && (
              <div className="ml-7 pl-3 mt-4 border-l-2 border-neutral-light space-y-4">
                <div>
                  <label htmlFor="personal_physician" className="block text-sm font-medium text-neutral-dark mb-1">
                    Primary Care Doctor's Name:
                  </label>
                  <input
                    id="personal_physician"
                    name="personal_physician"
                    value={consentData.personal_physician}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded border-neutral focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    disabled={isSubmitting}
                    placeholder="Doctor's Name"
                  />
                </div>
                <div>
                  <label htmlFor="physician_number" className="block text-sm font-medium text-neutral-dark mb-1">
                    Primary Care Doctor's Phone:
                  </label>
                  <input
                    id="physician_number"
                    name="physician_number"
                    value={consentData.physician_number}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded border-neutral focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    disabled={isSubmitting}
                    placeholder="Phone (e.g. 555-555-5555)"
                  />
                </div>
              </div>
            )}

            {/* 3. Designate Another Person */}
            <div className="flex items-center pt-6 border-t border-neutral-light">
              <input
                type="checkbox"
                id="additional_name_check"
                name="additional_name_check"
                onChange={handleCheckboxChange}
                checked={uiData.additional_name_check}
                className={checkBoxClasses}
                disabled={isSubmitting}
              />
              <label htmlFor="additional_name_check" className="ml-3 block text-sm text-neutral-dark">
                I wish to designate another person (e.g., spouse, caregiver) to receive information about my treatment and progress.
              </label>
            </div>

            {/* 3a. Designated Person Info */}
            {uiData.additional_name_check && (
              <div className="ml-7 pl-3 mt-4 border-l-2 border-neutral-light space-y-4">
                <div>
                  <label htmlFor="additional_name" className="block text-sm font-medium text-neutral-dark mb-1">
                    Designated Person's Name:
                  </label>
                  <input
                    id="additional_name"
                    name="additional_name"
                    value={consentData.additional_name}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded border-neutral focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    disabled={isSubmitting}
                    placeholder="Full Name"
                  />
                </div>
                <div>
                  <label htmlFor="additional_phone" className="block text-sm font-medium text-neutral-dark mb-1">
                    Designated Person's Phone:
                  </label>
                  <IMaskInput
                    id="additional_phone"
                    name="additional_phone"
                    mask="000-000-0000"
                    unmask={true}
                    value={consentData.additional_phone}
                    onAccept={(value) => handleInputChange({ target: { name: 'additional_phone', value } })}
                    className="w-full p-2 border rounded border-neutral focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    disabled={isSubmitting}
                    placeholder="Phone (e.g. 555-555-5555)"
                  />
                </div>
                <div>
                  <label htmlFor="additional_email" className="block text-sm font-medium text-neutral-dark mb-1">
                    Designated Person's Email:
                  </label>
                  <input
                    id="additional_email"
                    name="additional_email"
                    value={consentData.additional_email}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded border-neutral focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    disabled={isSubmitting}
                    placeholder="Email address"
                  />
                </div>
              </div>
            )}

            {/* 4. Opt-In to Communications */}
            <div className="flex items-center pt-6 border-t border-neutral-light">
              <input
                type="checkbox"
                id="is_opt_in"
                name="is_opt_in"
                checked={consentData.is_opt_in}
                onChange={handleCheckboxChange}
                className={checkBoxClasses}
                disabled={isSubmitting}
              />
              <label htmlFor="is_opt_in" className="ml-3 block text-sm text-neutral-dark">
                RediMD may contact me by email, phone, or text messages about my health. Carrier charges may apply.
              </label>
            </div>

            {/* 5. Accept Terms & Conditions (REQUIRED) */}
            <div className="flex items-center pt-6 border-t border-neutral-light">
              <input
                type="checkbox"
                id="terms_accepted"
                name="terms_accepted"
                checked={consentData.terms_accepted}
                onChange={handleCheckboxChange}
                className={checkBoxClasses}
                required
                disabled={isSubmitting}
              />
              <label htmlFor="terms_accepted" className="ml-3 block text-sm text-neutral-dark">
                I have read and agree to the RediMD <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms & Conditions</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a>. <span className="text-red-600">* Required</span>
              </label>
            </div>
          </div>
        {/* Buttons */}
          {(localError || error) && <p className="text-red-500 text-sm mb-2 text-center pb-4">{isWorkflowStep ? error : localError}</p>}
          <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 pb-4">
              { !isWorkflowStep && (<button
                  type="button"
                  onClick={onSaveComplete}
                  className={`w-full sm:w-1/4 bg-neutral-light text-neutral-dark p-3 rounded hover:bg-neutral-dark hover:text-white transition duration-200 font-semibold ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={isSubmitting}
              >
                  {isSubmitting ? '' : 'Close'}
              </button>
              )}        

            <button
              type="submit"
              className={`${isWorkflowStep ? 'w-full':'w-full sm:w-1/4'} bg-button text-white p-3 rounded-md hover:bg-button-light transition duration-200 font-semibold ${(isSubmitting || !consentData.terms_accepted) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={isSubmitting || !consentData.terms_accepted}
            >
              {isSubmitting ? 'Saving...' : (isWorkflowStep ? 'Save & Continue' : 'Consent Info')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default Consent;
